import { useHistory } from "react-router";

import {
  type BadgeProps,
  Button,
  Link,
  Text,
  toastError,
  useModal,
} from "@alpacahq/ui/old";

import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import {
  useGetHYCAccountStatus,
  useGetHYCAprTierOffer,
} from "~/v2/api/hooks/useGetHighYieldCash";
import {
  type Account,
  type PatchAccountRequest,
  patchAccount,
} from "~/v2/api/rest/account";
import { useLocalStorage } from "~/v2/hooks/useLocalStorage";
import { Path } from "~/v2/path";
import { getHighYieldCashLocalStorageKey } from "../../highYieldCash/util";
import { FeatureCard } from "./FeatureCard";

export const SWEEP_PROGRAM_TERMS_AND_CONDITIONS =
  "https://files.alpaca.markets/disclosures/Alpaca+FDIC+Bank+Sweep+Program+Terms+and+Conditions.pdf";

export const HighYieldCard = ({ account }: { account?: Account }) => {
  const history = useHistory();

  const [hycLocalStorage] = useLocalStorage<string>(
    getHighYieldCashLocalStorageKey(account?.id),
  );
  const hasApplied = hycLocalStorage === "true";

  const { hycStatus, hycApyString } = useGetHYCAccountStatus({
    accountID: account?.id,
  });

  const { hycApyString: hycApyOfferString } = useGetHYCAprTierOffer({
    accountID: account?.id,
  });

  const unEnrollModal = useModal({
    title: "Unenroll from High-Yield Cash",
    content: (
      <Text>
        You will be unenrolled from the High-Yield Cash product. Changes will be
        reflected on your account within 1-2 business days.
        <br />
        <br />
        Please reach out to{" "}
        <a href="mailto:support@alpaca.markets" className="underline">
          support@alpaca.markets
        </a>{" "}
        if you have any questions.
      </Text>
    ),
    controls: (
      <Button
        onClick={() => {
          unEnrollModal.toggle();
        }}
      >
        OK
      </Button>
    ),
  });

  const accountMutation = useMutationRequest(
    (payload: Partial<PatchAccountRequest>) =>
      patchAccount(account?.id ?? "", payload),
    {
      onSuccess: () => {
        unEnrollModal.toggle();
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Error: ${errorMessage}`);
      },
    },
  );

  const onUnenrollClick = () => {
    accountMutation.mutate({
      cash_interest: [
        {
          currency: "USD",
          status: "INACTIVE",
        },
      ],
    });
  };

  const onEnrollClick = () => history.push(Path.ROUTE_HIGH_YIELD_ENROLL);

  const badge: { variant: BadgeProps["variant"]; text: string } | undefined =
    hycStatus === "ACTIVE"
      ? {
          variant: "purple",
          text: `Enabled`,
        }
      : undefined;

  const isEnrolled = hycStatus === "ACTIVE" || hycStatus === "PENDING_CHANGE";

  const body = (
    <div className="flex w-full flex-col gap-4">
      <Text className="flex w-full items-center justify-between gap-2 text-sm">
        <span>
          Annual Percentage Yield (APY)<sup>*</sup>
        </span>
        <span>{isEnrolled ? hycApyString : hycApyOfferString}%</span>
      </Text>

      {(hycStatus === "INACTIVE" || hycStatus === null) && (
        <Text className="flex w-full items-center justify-between gap-2 text-sm">
          <span>Protected by FDIC Insurance</span>
          <span>$1,000,000</span>
        </Text>
      )}

      {isEnrolled && (
        <Text className="text-sm">
          You're earning interest on uninvested cash in your brokerage account.
          Interest is paid on the last business day of the month.
        </Text>
      )}

      <Link
        href={SWEEP_PROGRAM_TERMS_AND_CONDITIONS}
        className="block text-blue-700 underline"
      >
        Terms and Conditions
      </Link>
    </div>
  );

  const button = {
    text:
      hycStatus === "ACTIVE"
        ? "Unenroll from High-Yield Cash"
        : hycStatus === "PENDING_CHANGE" || (hycStatus === null && hasApplied)
          ? "Pending Change"
          : "Enroll in High-Yield Cash",
    onClick: hycStatus === "ACTIVE" ? onUnenrollClick : onEnrollClick,
    disabled:
      hycStatus === "PENDING_CHANGE" || (hycStatus === null && hasApplied),
  };

  return (
    <>
      <FeatureCard
        title="High-Yield Cash"
        badge={badge}
        body={body}
        button={button}
        disclaimer={
          <>
            <sup>*</sup>Rates subject to change
          </>
        }
      />
      {unEnrollModal.component}
    </>
  );
};
